import React, { useState, useEffect, useRef, useCallback } from 'react';
import debounce from 'lodash/debounce';

/**
 * MergedLabel
 *
 * - Two text inputs side by side: [ leftValue ] - [ rightValue ]
 * - Each can interpret "??k" => multiply by 1000
 * - If typed < 100 => multiply by 1000
 * - If out of [min..max], revert to last valid
 * - Partial typing with 500ms (or 2s) debounce so user sees changes update after a short pause
 * - Press Enter => finalize immediately, Esc => revert
 * - If parent changes leftValue/rightValue externally, we re-sync local input unless user is editing
 */
export default function MergedLabel({
  leftValue,
  rightValue,
  onEditLeft,
  onEditRight,
  min = 0,
  max = 999999,
  formatValue,
  style,
  debounceMs = 2000, // or 2000 if you want a longer pause
}) {
  const [isEditing, setIsEditing] = useState(false);

  // Local input states
  const [leftInput, setLeftInput] = useState(String(leftValue));
  const [rightInput, setRightInput] = useState(String(rightValue));

  // Last valid states
  const [lastValidLeft, setLastValidLeft] = useState(leftValue);
  const [lastValidRight, setLastValidRight] = useState(rightValue);

  /**
   * Sync with external changes:
   * If parent changes leftValue/rightValue and we are *not* editing, re-sync.
   */
  useEffect(() => {
    if (!isEditing) {
      setLeftInput(String(leftValue));
      setRightInput(String(rightValue));
      setLastValidLeft(leftValue);
      setLastValidRight(rightValue);
    }
  }, [leftValue, rightValue, isEditing]);

  /**
   * parseAndRevert: replicate same logic as EditableThumbWithLabel
   * - If ends with 'k', multiply by 1000
   * - If < 100 => multiply by 1000
   * - If out of range => revert
   */
  const parseAndRevert = useCallback(
    (rawStr, lastValid) => {
      let str = rawStr.trim().toLowerCase();
      const hasK = /k$/.test(str);
      if (hasK) {
        str = str.replace(/k$/, '');
      }
      let parsed = parseFloat(str.replace(/[^\d.-]/g, ''));
      if (isNaN(parsed)) {
        // revert if invalid
        return lastValid;
      }
      if (hasK) {
        parsed *= 1000;
      } else if (parsed < 1000) {
        parsed *= 1000;
      }
      if (parsed < min || parsed > max) {
        return lastValid;
      }
      return parsed;
    },
    [min, max]
  );

  // Optional formatting: e.g. if >= 1000 => "XXk"
  const formatCompact = useCallback((num) => {
    if (num >= 1000) {
      return Math.round(num / 1000) + 'k';
    }
    return String(num);
  }, []);

  /**
   * Debounced updaters for left and right.
   * Called as user types, so you see partial changes after a short pause.
   */
  const debouncedLeft = useRef(
    debounce((rawStr) => {
      const parsed = parseAndRevert(rawStr, lastValidLeft);
      if (parsed !== lastValidLeft) {
        setLastValidLeft(parsed);
        onEditLeft(parsed);
      }
      setLeftInput(String(parsed));
    }, debounceMs)
  ).current;

  const debouncedRight = useRef(
    debounce((rawStr) => {
      const parsed = parseAndRevert(rawStr, lastValidRight);
      if (parsed !== lastValidRight) {
        setLastValidRight(parsed);
        onEditRight(parsed);
      }
      setRightInput(String(parsed));
    }, debounceMs)
  ).current;

  /** 
   * Handle changes on each input
   * We'll store the typed text, but after 500ms we parse & call onEditLeft/onEditRight
   */
  const handleLeftChange = (e) => {
    const val = e.target.value;
    setLeftInput(val);
    debouncedLeft(val);
  };
  const handleRightChange = (e) => {
    const val = e.target.value;
    setRightInput(val);
    debouncedRight(val);
  };

  /**
   * handleFocus: set isEditing=true, optionally select text
   */
  const handleLeftFocus = (e) => {
    setIsEditing(true);
    e.target.select();
  };
  const handleRightFocus = (e) => {
    setIsEditing(true);
    e.target.select();
  };

  /**
   * Press Enter => finalize immediately
   * Press Esc => revert
   */
  const handleLeftKeyDown = (e) => {
    if (e.key === 'Enter') {
      finalizeBoth();
      e.target.blur();
    } else if (e.key === 'Escape') {
      revertBoth();
      e.target.blur();
    }
  };
  const handleRightKeyDown = (e) => {
    if (e.key === 'Enter') {
      finalizeBoth();
      e.target.blur();
    } else if (e.key === 'Escape') {
      revertBoth();
      e.target.blur();
    }
  };

  /**
   * On blur => finalize
   */
  const handleBlur = () => {
    // If both inputs have lost focus, end editing
    // We'll check for a slight delay or track focuses if needed
    // For simplicity, we just finalize now
    finalizeBoth();
    setIsEditing(false);
  };

  /**
   * finalizeBoth: flush debounces & parse final
   */
  const finalizeBoth = () => {
    debouncedLeft.flush();
    debouncedRight.flush();
    // parse final values in case user typed last char
    const finalLeft = parseAndRevert(leftInput, lastValidLeft);
    const finalRight = parseAndRevert(rightInput, lastValidRight);

    if (finalLeft !== lastValidLeft) {
      setLastValidLeft(finalLeft);
      onEditLeft(finalLeft);
    }
    if (finalRight !== lastValidRight) {
      setLastValidRight(finalRight);
      onEditRight(finalRight);
    }

    // update displayed text
    setLeftInput(formatCompact(finalLeft));
    setRightInput(formatCompact(finalRight));
  };

  /**
   * revertBoth: revert to last valid & end editing
   */
  const revertBoth = () => {
    debouncedLeft.cancel();
    debouncedRight.cancel();
    setLeftInput(String(lastValidLeft));
    setRightInput(String(lastValidRight));
    setIsEditing(false);
  };

  /**
   * Display in read-only mode
   */
  const leftDisplay = formatValue
    ? formatValue(leftValue)
    : String(leftValue);
  const rightDisplay = formatValue
    ? formatValue(rightValue)
    : String(rightValue);

  return (
    <div style={style} className="pointer-events-auto relative">
      {isEditing ? (
        <div className="flex flex-row items-center gap-2 whitespace-nowrap">
          <input
            type="text"
            value={leftInput}
            onChange={handleLeftChange}
            onFocus={handleLeftFocus}
            onKeyDown={handleLeftKeyDown}
            onBlur={handleBlur}
            className="bg-white text-gray-800 text-center
                       border border-slate-300 outline-none w-20 rounded-full"
            style={{ padding: '6px 4px', fontSize: '1rem' }}
          />
          <span>-</span>
          <input
            type="text"
            value={rightInput}
            onChange={handleRightChange}
            onFocus={handleRightFocus}
            onKeyDown={handleRightKeyDown}
            onBlur={handleBlur}
            className="bg-white text-gray-800 text-center
                       border border-slate-300 outline-none w-20 rounded-full"
            style={{ padding: '6px 4px', fontSize: '1rem' }}
          />
        </div>
      ) : (
        <div
        onClick={() => setIsEditing(true)}
        className="cursor-pointer inline-block"
      >
        <div className="
          relative bg-white px-2 py-1 border border-slate-700
          rounded-full shadow text-center w-max h-[38px] flex items-center justify-center
        ">
          <span className="text-gray-800 text-base whitespace-nowrap">
            {leftDisplay} - {rightDisplay}
          </span>
        </div>
      </div>
      )}
    </div>
  );
}